import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { TemplateResponse } from '@/api/generated/model';
import {
  getReceituariosControllerListarTemplatesQueryKey,
  useReceituariosControllerAtualizarTemplate,
  useReceituariosControllerCriarTemplate,
  useReceituariosControllerExcluirTemplate,
  useReceituariosControllerListarTemplates,
} from '@/api/generated/receituarios/receituarios';
import { ApiError } from '@/api/http-client';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  podeEditar: boolean;
}

/**
 * Modelos de receituário.
 *
 * ⚠️ Na interface é "modelo"; no código e na API continua `template`
 * (`/receituarios/templates`). O rename foi de vocabulário do produto, e
 * arrastá-lo até os hooks gerados por orval seria reescrever a cada
 * `npm run api` — o mapeamento vive aqui, na fronteira.
 *
 * Um dialog só, com a lista e o editor embutido — em vez de tela própria. O
 * modelo não tem vida fora do receituário: só existe para ser aplicado num, e
 * uma rota separada obrigaria a sair do fluxo para voltar.
 */
export function TemplatesDialogo({ aberto, aoFechar, podeEditar }: Props) {
  const queryClient = useQueryClient();
  const criar = useReceituariosControllerCriarTemplate();
  const atualizar = useReceituariosControllerAtualizarTemplate();
  const excluir = useReceituariosControllerExcluirTemplate();

  const { data, isPending, isError, refetch } = useReceituariosControllerListarTemplates({
    query: { enabled: aberto },
  });

  const [emEdicao, setEmEdicao] = useState<TemplateResponse | null>(null);
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const editorAberto = criando || Boolean(emEdicao);

  function abrirNovo() {
    setEmEdicao(null);
    setNome('');
    setConteudo('');
    setCriando(true);
  }

  function abrirEdicao(template: TemplateResponse) {
    setCriando(false);
    setEmEdicao(template);
    setNome(template.nome);
    setConteudo(template.conteudo);
  }

  function fecharEditor() {
    setCriando(false);
    setEmEdicao(null);
  }

  async function invalidar() {
    await queryClient.invalidateQueries({
      queryKey: getReceituariosControllerListarTemplatesQueryKey(),
    });
  }

  async function salvar() {
    if (nome.trim().length < 2) {
      toast.error('Informe um nome com ao menos 2 caracteres.');
      return;
    }
    if (!conteudo.trim()) {
      toast.error('O modelo não pode ficar vazio.');
      return;
    }

    setEnviando(true);
    try {
      const corpo = { nome: nome.trim(), conteudo: conteudo.trim() };
      if (emEdicao) {
        await atualizar.mutateAsync({ templateId: emEdicao.id, data: corpo });
      } else {
        await criar.mutateAsync({ data: corpo });
      }
      await invalidar();
      toast.success(emEdicao ? 'Modelo atualizado.' : 'Modelo criado.');
      fecharEditor();
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível salvar o modelo.');
    } finally {
      setEnviando(false);
    }
  }

  async function remover(template: TemplateResponse) {
    try {
      await excluir.mutateAsync({ templateId: template.id });
      await invalidar();
      toast.success('Modelo excluído.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Modelos de receituário</DialogTitle>
          <DialogDescription>
            Salve textos que você utiliza com frequência e reutilize-os sempre que precisar.
          </DialogDescription>
        </DialogHeader>

        {editorAberto ? (
          <div className="space-y-4">
            <Campo id="nomeTemplate" rotulo="Nome" obrigatorio>
              <Input
                id="nomeTemplate"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className={CLASSE_CONTROLE}
              />
            </Campo>

            <Campo id="conteudoTemplate" rotulo="Texto" obrigatorio>
              <Textarea
                id="conteudoTemplate"
                rows={8}
                value={conteudo}
                onChange={(e) => setConteudo(e.target.value)}
                className={CLASSE_CONTROLE}
              />
            </Campo>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={fecharEditor}>
                Voltar
              </Button>
              <Button onClick={() => void salvar()} disabled={enviando}>
                {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            {podeEditar && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={abrirNovo}>
                  <Plus className="w-4 h-4" />
                  Novo modelo
                </Button>
              </div>
            )}

            {isPending ? (
              <EstadoCarregando rotulo="Carregando modelos..." />
            ) : isError || !data ? (
              <EstadoErro aoTentarDeNovo={() => void refetch()} />
            ) : data.itens.length === 0 ? (
              <EstadoVazio
                titulo="Nenhum modelo salvo"
                descricao="Crie seu primeiro modelo e agilize seus próximos receituários."
              />
            ) : (
              <ul className="space-y-2 max-h-80 overflow-y-auto">
                {data.itens.map((template) => (
                  <li
                    key={template.id}
                    className="flex items-start justify-between gap-3 rounded-md border border-brand/15 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{template.nome}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {template.conteudo}
                      </p>
                    </div>
                    {podeEditar && (
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Editar ${template.nome}`}
                          onClick={() => abrirEdicao(template)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Excluir ${template.nome}`}
                          onClick={() => void remover(template)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={aoFechar}>
                Fechar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
